import React, { useEffect, useState } from "react";
import { ChevronDown, Search, Heart, ShoppingCart, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import CategorySkeleton from "./CategorySkeleton";
import ProductSkeleton from "./ProductSkeleton";
import AddToCartButton from "./AddToCart";
import WhatsAppButton from "./WhatsAppButton";
import Footer from "./Footer";
import Carousel from "../carousel/Carousel";
import { doc, collection, getDocs, addDoc } from "firebase/firestore";
import Header from "./Header";
import MetalPriceTracker from "./MetalPriceTracker";
import ElegantBackground from "./ElegantBackground";
import { getUserId } from "../utils/utils";

const LandingPage = () => {
  const navigate = useNavigate();
  const userId = getUserId();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState({
    categories: true,
    products: true,
  });
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesSnapshot = await getDocs(collection(db, "categories"));
        const categoriesData = [];
        categoriesSnapshot.forEach((doc) => {
          categoriesData.push({ id: doc.id, ...doc.data() });
        });
        setCategories(categoriesData);
        setLoading((prev) => ({ ...prev, categories: false }));

        // Fetch products
        const productsSnapshot = await getDocs(
          collection(db, "featuredProducts")
        );
        const productsData = [];
        productsSnapshot.forEach((doc) => {
          productsData.push({ id: doc.id, ...doc.data() });
        });
        setProducts(productsData);
        setLoading((prev) => ({ ...prev, products: false }));
      } catch (error) {
        console.error("Error fetching data:", error);
        // Handle error appropriately
      }
    };

    fetchData();
  }, []);

  const addToCart = async (product) => {
    try {
      const cartRef = collection(db, `users/${userId}/cart`);
      await addDoc(cartRef, { ...product, quantity: 1 });
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  };

  const addToWishlist = async (product) => {
    try {
      const cartRef = collection(db, `users/${userId}/wishlist`);
      await addDoc(cartRef, { ...product, quantity: 1 });
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      throw error;
    }
  };

  const renderCategoriesSection = () => (
    <section className="py-16 bg-transparent">
      <div className="container mx-auto px-6">
        <h3 className="text-3xl font-serif text-center mb-12">
          Our Collections
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {loading.categories
            ? // Show skeletons while loading
              [...Array(8)].map((_, index) => <CategorySkeleton key={index} />)
            : // Show actual categories
              categories.map((category) => (
                <div
                  key={category.id}
                  className="group relative overflow-hidden rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl cursor-pointer"
                  onClick={() => navigate(`/category/${category.id}`)}
                >
                  <div className="aspect-square">
                    <img
                      src={category.imageUrl}
                      alt={category.name}
                      className="object-cover w-full h-full transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h4 className="text-xl font-medium mb-2">
                      {category.name}
                    </h4>
                    {category.subCategories && (
                      <ul className="space-y-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {category.subCategories.map((subCat) => (
                          <li
                            key={subCat}
                            className="text-sm hover:text-gray-300 cursor-pointer"
                          >
                            {subCat}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
        </div>
      </div>
    </section>
  );

  const renderProductsSection = () => (
    <section className="py-16 bg-transparent">
      <div className="container mx-auto px-6">
        <h3 className="text-3xl font-serif text-center mb-12">
          Featured Collection
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {loading.products
            ? // Show skeletons while loading
              [...Array(8)].map((_, index) => <ProductSkeleton key={index} />)
            : // Show actual products
              products.map((product) => (
                <div
                  key={product.id}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div
                      className="absolute top-4 right-4"
                      onClick={() => addToWishlist(product)}
                    >
                      <Heart className="w-6 h-6 text-white hover:text-red-500 cursor-pointer transition-colors" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      {product.name}
                    </h4>
                    <p className="text-xl font-serif text-gray-800 mb-4">
                      ₹{product.price.toLocaleString("en-IN")}
                    </p>
                    <AddToCartButton onClick={() => addToCart(product)} />
                  </div>
                </div>
              ))}
        </div>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <Header />
      <MetalPriceTracker />

      {/* Hero Section - Lightened with elegant accents */}
      <section className="relative h-[80vh] overflow-hidden bg-gradient-to-r from-purple-100 to-purple-50">
        <div className="absolute inset-0 bg-[url('https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTDZkGS-kNqBg4qAguhFK_dgS7ffBPKYL70Ug&s')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/50" />
        <div className="absolute inset-0">
          {/* Decorative overlay pattern */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                   linear-gradient(to right, rgba(147, 51, 234, 0.05) 1px, transparent 1px),
                   linear-gradient(to bottom, rgba(147, 51, 234, 0.05) 1px, transparent 1px)
                 `,
              backgroundSize: "50px 50px",
            }}
          />
        </div>
        <div className="relative container mx-auto px-6 h-full flex flex-col justify-center items-center text-center">
          <h2 className="text-5xl md:text-6xl font-serif mb-6 leading-tight text-purple-900">
            Timeless Silver
            <br />
            Eternal Elegance
          </h2>
          <p className="text-xl md:text-2xl text-purple-800 mb-8 max-w-2xl">
            Discover our exquisite collection of artisanal silver jewelry, where
            tradition meets contemporary design.
          </p>
          <button
            className="px-8 py-4 bg-purple-700 text-white rounded-full text-lg font-medium hover:bg-purple-600 transition-colors duration-200 transform hover:scale-105 shadow-lg"
            onClick={() => {
              navigate("/search");
            }}
          >
            Explore Collection
          </button>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <h3 className="text-3xl font-serif text-center mb-12 text-purple-900">
            Our Collections
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {loading.categories
              ? [...Array(8)].map((_, index) => (
                  <CategorySkeleton key={index} />
                ))
              : categories.map((category) => (
                  <div
                    key={category.id}
                    className="group relative overflow-hidden rounded-xl shadow-md transition-all duration-300 hover:shadow-xl cursor-pointer bg-white"
                    onClick={() => navigate(`/category/${category.name}`)}
                  >
                    <div className="aspect-square">
                      <img
                        src={category.imageUrl}
                        alt={category.name}
                        className="object-cover w-full h-full transform group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-purple-900/70 to-transparent" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      <h4
                        className="text-xl font-medium mb-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/category/${category.name}`);
                        }}
                      >
                        {category.name}
                      </h4>
                      {category.subCategories && (
                        <ul className="space-y-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {category.subCategories.map((subCat) => (
                            <li
                              key={subCat}
                              className="text-sm hover:text-purple-200 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/category/${category.name}/${subCat}`
                                );
                              }}
                            >
                              {subCat}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16 bg-purple-50">
        <div className="container mx-auto px-6">
          <h3 className="text-3xl font-serif text-center mb-12 text-purple-900">
            Featured Collection
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {loading.products
              ? [...Array(8)].map((_, index) => <ProductSkeleton key={index} />)
              : products.map((product) => (
                  <div
                    key={product.id}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                    onClick={() =>
                      navigate("/product-details", { state: { product } })
                    }
                  >
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={product?.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                      />
                      <div
                        className="absolute top-4 right-4"
                        onClick={(e) => {
                          e.stopPropagation();
                          addToWishlist(product);
                        }}
                      >
                        <Heart className="w-6 h-6 text-white hover:text-red-500 cursor-pointer transition-colors" />
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className="text-lg font-medium text-purple-900 mb-2">
                        {product.name}
                      </h4>
                      <p className="text-xl font-serif text-purple-800 mb-4">
                        ₹{product.price.toLocaleString("en-IN")}
                      </p>
                      <AddToCartButton
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(product);
                        }}
                      />
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </section>

      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default LandingPage;
